/**
 * Little characters, drawn in crayon like everything else.
 *
 * They are deliberately abstract: a wobbly blob, two dots and a curve. That is
 * not a shortcut — a face with too much detail lands in the uncanny place
 * where "cute" turns to "grotesque", and a five-year-old is the harshest
 * possible judge of that. Two dots and a curve read as friendly at any size,
 * on any colour, in every mood, and they cannot go wrong.
 *
 * Every feature is derived from a seed inside bounds that guarantee a face
 * that works: eyes always inside the head, always level, always a pair. What
 * varies is proportion and colour, which is enough for hundreds of them to
 * feel like different somebodies.
 */

import type { Vec } from './vec.ts';

export type Mood = 'calm' | 'happy' | 'surprised' | 'sleepy';

/**
 * Body shapes, taken from the generated design sheets: they are what stops a
 * crowd of these reading as one character in several colours. A tall capsule
 * and a low pebble are recognisably different somebodies with the same face.
 */
export type Form = 'round' | 'tall' | 'wide' | 'arch' | 'pebble';

export const FORMS: Form[] = ['round', 'tall', 'wide', 'arch', 'pebble'];

/** How each form stretches the body. The narrowest sets the eye bounds. */
const PROPORTIONS: Record<Form, { rx: number; ry: number; flatBottom: boolean }> = {
  round: { rx: 1, ry: 1, flatBottom: false },
  tall: { rx: 0.78, ry: 1.16, flatBottom: false },
  wide: { rx: 1.16, ry: 0.8, flatBottom: false },
  arch: { rx: 0.9, ry: 1.06, flatBottom: true },
  pebble: { rx: 1.1, ry: 0.88, flatBottom: false },
};

/** The narrowest body any form can have — what the eyes must fit inside. */
export const NARROWEST = 0.78;

export interface Character {
  seed: number;
  color: string;
  form: Form;
  /** How far from round the body is, 0..1. */
  lumpiness: number;
  /** Eye separation as a fraction of the body width. */
  eyeGap: number;
  eyeSize: number;
  /** How high the eyes sit, as a fraction of the body height. */
  eyeHeight: number;
  /** Smile depth, 0..1. */
  smile: number;
  /** A little tuft on top, because some of them have one. */
  tuft: boolean;
  /** Phase offset so a crowd of them does not bob in lockstep. */
  phase: number;
}

/** Deterministic and cheap — the same seed always makes the same character. */
function random(seed: number): () => number {
  let state = (seed | 0) || 1;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Every bound here is chosen so there is no seed that produces something
 * unpleasant. The eyes cannot leave the head, cannot touch each other, and
 * cannot be so large they read as a stare.
 */
export function characterFrom(seed: number, color: string): Character {
  const rand = random(seed);
  return {
    seed,
    color,
    form: FORMS[Math.floor(rand() * FORMS.length) % FORMS.length],
    lumpiness: lerp(0.04, 0.16, rand()),
    // Bounded against the NARROWEST form, so the eyes stay inside the body
    // even on the slimmest one.
    eyeGap: lerp(0.24, 0.42, rand()),
    eyeSize: lerp(0.09, 0.15, rand()),
    eyeHeight: lerp(-0.16, 0.02, rand()),
    smile: lerp(0.35, 1, rand()),
    tuft: rand() > 0.55,
    phase: rand() * Math.PI * 2,
  };
}

export interface DrawOptions {
  x: number;
  y: number;
  /** Radius of the body. */
  size: number;
  /** Seconds, for blinking and bobbing. */
  t?: number;
  mood?: Mood;
  /** Squash from a recent landing, 0..1. */
  squash?: number;
  /** Turn the whole character. */
  angle?: number;
}

/** The wobbly outline of the body — a shape that was drawn by hand. */
function bodyPath(ch: Character, size: number): Vec[] {
  const { rx, ry, flatBottom } = PROPORTIONS[ch.form];
  const points: Vec[] = [];
  const steps = 30;
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    const r = size * (1 + Math.sin(a * 3 + ch.seed) * ch.lumpiness + Math.sin(a * 5 + ch.seed * 2) * ch.lumpiness * 0.4);
    const y = Math.sin(a) * r * ry;
    points.push({
      x: Math.cos(a) * r * rx,
      // An arch sits flat on the floor, like a little door.
      y: flatBottom ? Math.min(y, size * 0.86) : y,
    });
  }
  return points;
}

/** Blinks every few seconds, out of step with its neighbours. */
function blinkAmount(ch: Character, t: number): number {
  const cycle = 3.4 + (ch.seed % 7) * 0.4;
  const at = (t + ch.phase) % cycle;
  return at < 0.14 ? 1 - Math.abs(at - 0.07) / 0.07 : 0;
}

export function drawCharacter(ctx: CanvasRenderingContext2D, ch: Character, o: DrawOptions): void {
  const t = o.t ?? 0;
  const mood = o.mood ?? 'calm';
  const squash = Math.max(0, Math.min(1, o.squash ?? 0));
  const size = o.size;

  ctx.save();
  ctx.translate(o.x, o.y);
  if (o.angle) ctx.rotate(o.angle);
  // Squash and stretch: wider and shorter the instant it lands.
  if (squash > 0.001) ctx.scale(1 + squash * 0.22, 1 - squash * 0.2);

  // Body.
  const path = bodyPath(ch, size);
  ctx.beginPath();
  ctx.moveTo(path[0].x, path[0].y);
  for (const p of path.slice(1)) ctx.lineTo(p.x, p.y);
  ctx.closePath();
  ctx.fillStyle = ch.color;
  ctx.fill();
  ctx.lineWidth = Math.max(2, size * 0.11);
  ctx.strokeStyle = 'rgba(44, 37, 69, 0.85)';
  ctx.lineJoin = 'round';
  ctx.stroke();

  if (ch.tuft) {
    ctx.beginPath();
    ctx.moveTo(0, -size * 0.95);
    ctx.quadraticCurveTo(size * 0.16, -size * 1.34, size * 0.3, -size * 1.12);
    ctx.lineWidth = Math.max(2, size * 0.1);
    ctx.lineCap = 'round';
    ctx.stroke();
  }

  paintFace(ctx, ch, size, t, mood);
  ctx.restore();
}

/**
 * Just the face, for when the body is something else — a shape the child drew
 * themselves. Their drawing stays exactly as they made it and simply acquires
 * somebody looking out of it.
 */
export function drawFace(ctx: CanvasRenderingContext2D, ch: Character, o: DrawOptions): void {
  const squash = Math.max(0, Math.min(1, o.squash ?? 0));
  ctx.save();
  ctx.translate(o.x, o.y);
  if (o.angle) ctx.rotate(o.angle);
  if (squash > 0.001) ctx.scale(1 + squash * 0.22, 1 - squash * 0.2);
  paintFace(ctx, ch, o.size, o.t ?? 0, o.mood ?? 'calm');
  ctx.restore();
}

/** The eyes and mouth, in a context already positioned and scaled. */
function paintFace(ctx: CanvasRenderingContext2D, ch: Character, size: number, t: number, mood: Mood): void {
  // Eyes. A blink is a squash, not a disappearance, so it reads as a blink.
  const blink = mood === 'sleepy' ? 0.86 : blinkAmount(ch, t);
  const open = mood === 'surprised' ? 1.35 : 1;
  const eyeX = size * ch.eyeGap;
  const eyeY = size * ch.eyeHeight;
  const eyeR = size * ch.eyeSize * open;

  ctx.fillStyle = 'rgba(28, 22, 46, 0.92)';
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(side * eyeX, eyeY);
    ctx.scale(1, Math.max(0.08, 1 - blink));
    ctx.beginPath();
    ctx.arc(0, 0, eyeR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Mouth: one curve, deeper when happy.
  const happy = mood === 'happy' ? 1.5 : mood === 'sleepy' ? 0.45 : 1;
  const mouthY = eyeY + size * 0.32;
  const width = size * 0.3;
  ctx.beginPath();
  if (mood === 'surprised') {
    ctx.arc(0, mouthY + size * 0.04, size * 0.11, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(28, 22, 46, 0.9)';
    ctx.fill();
  } else {
    ctx.moveTo(-width, mouthY);
    ctx.quadraticCurveTo(0, mouthY + size * 0.3 * ch.smile * happy, width, mouthY);
    ctx.lineWidth = Math.max(2, size * 0.08);
    ctx.strokeStyle = 'rgba(28, 22, 46, 0.9)';
    ctx.lineCap = 'round';
    ctx.stroke();
  }
}

/**
 * How far a resting character bobs, so a row of them looks alive rather than
 * placed. Small on purpose: a crowd that heaves is distracting.
 */
export function bobOffset(ch: Character, t: number, size: number): number {
  return Math.sin(t * 2 + ch.phase) * size * 0.06;
}
